import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { adminService, UserWithStats, DashboardStats } from '../services/adminService';
import {
    Users,
    UserCheck,
    UserX,
    Crown,
    CreditCard,
    Folder,
    CheckCircle,
    Clock,
    FileText,
    BookOpen,
    TestTube,
    ListChecks,
    Search,
    ChevronRight,
    TrendingUp,
    Activity,
    Shield,
    ShieldAlert,
    Power,
    X,
    ExternalLink,
    Calendar,
    Building2,
    Phone
} from 'lucide-react';
import { toast } from 'react-hot-toast';

export const AdminPanelPage: React.FC = () => {
    const navigate = useNavigate();
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const [users, setUsers] = useState<UserWithStats[]>([]);
    const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterRole, setFilterRole] = useState<'all' | 'admin' | 'user'>('all');
    const [filterSubscription, setFilterSubscription] = useState<'all' | 'F' | 'P' | 'S'>('all');
    const [modelRouting, setModelRouting] = useState<any[]>([]);


    // Section and Coupon states
    const [activeSection, setActiveSection] = useState<'users' | 'coupons' | 'routing'>('users');
    const [routingTaskFilter, setRoutingTaskFilter] = useState('all');
    const [routingProviderFilter, setRoutingProviderFilter] = useState('all');
    const [coupons, setCoupons] = useState<any[]>([]);
    const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
    const [couponForm, setCouponForm] = useState({
        id: null as number | null,
        couponName: '',
        discountPercent: 50,
        redeemsCount: 1,
        planType: 'starter' as 'starter' | 'intelligence',
        isActive: true
    });

    // Model Routing states
    const [isRoutingModalOpen, setIsRoutingModalOpen] = useState(false);
    const [routingForm, setRoutingForm] = useState({
        id: null as number | null,
        task_name: '',
        model_id: '',
        fallback_models: '',
        score: 0
    });

    // Selected user for modal details
    const [selectedUser, setSelectedUser] = useState<UserWithStats | null>(null);
    const [userDetails, setUserDetails] = useState<{ projects: any[] } | null>(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [userTokenUsage, setUserTokenUsage] = useState<any[]>([]);
    const [loadingUserTokens, setLoadingUserTokens] = useState(false);
    const [selectedTab, setSelectedTab] = useState<'projects' | 'tokens'>('projects');
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
        confirmText: string;
        type: 'danger' | 'success' | 'warning';
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        confirmText: 'Confirm',
        type: 'warning'
    });

    useEffect(() => {
        // Check if user is admin
        if (currentUser?.role !== 'admin') {
            navigate('/dashboard');
            return;
        }

        fetchData();
    }, [currentUser, navigate]);

    const fetchData = async () => {
        try {
            setLoading(true);

            const [usersResponse, statsResponse, routingResponse] = await Promise.all([
                adminService.getAllUsers(),
                adminService.getDashboardStats(),
                adminService.getModelRouting()
            ]);

            if (usersResponse.success && usersResponse.data) {
                setUsers(usersResponse.data);
            }

            if (statsResponse.success && statsResponse.data) {
                setDashboardStats(statsResponse.data);
            }

            if (routingResponse?.success && routingResponse.data) {
                setModelRouting(routingResponse.data);
            }

            setError(null);
        } catch (err: any) {
            console.error('Error fetching admin data:', err);
            setError(err.response?.data?.message || 'Failed to load admin data');
        } finally {
            setLoading(false);
        }
    };

    const fetchCoupons = async () => {
        try {
            const response = await adminService.getCoupons();
            if (response.success && response.data) {
                setCoupons(response.data);
            }
        } catch (err: any) {
            console.error('Failed to load coupons:', err);
            toast.error('Failed to load coupons');
        }
    };

    useEffect(() => {
        if (activeSection === 'coupons') {
            fetchCoupons();
        }
    }, [activeSection]);

    const handleSaveCoupon = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!couponForm.couponName.trim()) {
            toast.error('Coupon name is required');
            return;
        }

        try {
            let res;
            if (couponForm.id) {
                res = await adminService.updateCoupon(couponForm.id, {
                    couponName: couponForm.couponName,
                    discountPercent: couponForm.discountPercent,
                    redeemsCount: couponForm.redeemsCount,
                    planType: couponForm.planType,
                    isActive: couponForm.isActive
                });
            } else {
                res = await adminService.createCoupon({
                    couponName: couponForm.couponName,
                    discountPercent: couponForm.discountPercent,
                    redeemsCount: couponForm.redeemsCount,
                    planType: couponForm.planType,
                    isActive: couponForm.isActive
                });
            }

            if (res.success) {
                toast.success(res.message || 'Coupon saved successfully');
                setIsCouponModalOpen(false);
                fetchCoupons();
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to save coupon');
        }
    };

    const handleSaveRouting = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!routingForm.id || !routingForm.model_id.trim()) {
            toast.error('Model ID is required');
            return;
        }

        try {
            const res = await adminService.updateModelRouting(routingForm.id, {
                model_id: routingForm.model_id,
                fallback_models: routingForm.fallback_models,
                score: routingForm.score
            });

            if (res.success) {
                toast.success(res.message || 'Routing updated successfully');
                setIsRoutingModalOpen(false);
                fetchData(); // refresh the routing matrix
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to update routing');
        }
    };

    const handleDeleteCoupon = async (coupon: any) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Coupon',
            message: `Are you sure you want to delete coupon ${coupon.couponName}? This action cannot be undone.`,
            confirmText: 'Confirm Deletion',
            type: 'danger',
            onConfirm: async () => {
                try {
                    const response = await adminService.deleteCoupon(coupon.id);
                    if (response.success) {
                        toast.success('Coupon deleted successfully');
                        fetchCoupons();
                    }
                } catch (err: any) {
                    toast.error(err.response?.data?.message || 'Failed to delete coupon');
                } finally {
                    setConfirmModal(prev => ({ ...prev, isOpen: false }));
                }
            }
        });
    };

    const handleToggleCouponActive = async (coupon: any) => {
        try {
            const res = await adminService.updateCoupon(coupon.id, {
                isActive: !coupon.isActive
            });
            if (res.success) {
                toast.success(`Coupon status updated!`);
                fetchCoupons();
            }
        } catch (err: any) {
            toast.error('Failed to update status');
        }
    };

    const viewUserTokenUsage = async (user: UserWithStats) => {
        // Instead of filtering the main page table, we now open the user modal
        // and switch to the token usage tab directly
        await viewUserDetails(user, 'tokens');
    };

    const handleToggleStatus = async (user: UserWithStats) => {
        if (user.id === currentUser?.id) {
            toast.error("You cannot deactivate your own account");
            return;
        }

        const action = user.isActive ? 'deactivate' : 'activate';

        setConfirmModal({
            isOpen: true,
            title: user.isActive ? 'Suspend Access' : 'Restore Access',
            message: `Are you sure you want to ${user.isActive ? 'deactivate' : 'activate'} this account?`,
            confirmText: user.isActive ? 'Confirm Deactivation' : 'Confirm Activation',
            type: user.isActive ? 'danger' : 'success',
            onConfirm: async () => {
                try {
                    const response = await adminService.toggleStatus(user.id);
                    if (response.success) {
                        toast.success(response.message || `User ${action}d successfully`);
                        // Update local state
                        setUsers(prev => prev.map(u => u.id === user.id ? { ...u, isActive: !u.isActive } : u));
                        if (selectedUser && selectedUser.id === user.id) {
                            setSelectedUser(prev => prev ? { ...prev, isActive: !prev.isActive } : null);
                        }
                    }
                } catch (err: any) {
                    toast.error(err.response?.data?.message || "Failed to update status");
                } finally {
                    setConfirmModal(prev => ({ ...prev, isOpen: false }));
                }
            }
        });
    };

    const handleUpdatePlan = async (user: UserWithStats, newPlan: 'free' | 'starter' | 'intelligence') => {
        if (user.planType === newPlan) return;

        setConfirmModal({
            isOpen: true,
            title: 'Change Matrix Tier',
            message: `Are you sure you want to transition ${user.username} to THE ${newPlan.toUpperCase()} TIER? This will reset their computation load and allowance accordingly.`,
            confirmText: 'Initiate Transition',
            type: 'warning',
            onConfirm: async () => {
                try {
                    const response = await adminService.updatePlan(user.id, newPlan);
                    if (response.success) {
                        toast.success(response.message || `Plan updated successfully`);
                        // Update local state
                        setUsers(prev => prev.map(u => u.id === user.id ? { 
                            ...u, 
                            planType: newPlan,
                            subscribed: newPlan === 'free' ? 'F' : 'S'
                        } : u));
                        if (selectedUser && selectedUser.id === user.id) {
                            setSelectedUser(prev => prev ? { 
                                ...prev, 
                                planType: newPlan,
                                subscribed: newPlan === 'free' ? 'F' : 'S'
                            } : null);
                        }
                    }
                } catch (err: any) {
                    toast.error(err.response?.data?.message || "Failed to update plan");
                } finally {
                    setConfirmModal(prev => ({ ...prev, isOpen: false }));
                }
            }
        });
    };

    const handleUpdateRole = async (user: UserWithStats, newRole: 'admin' | 'user') => {
        if (user.id === currentUser?.id && newRole === 'user') {
            toast.error("You cannot remove your own admin role");
            return;
        }

        setConfirmModal({
            isOpen: true,
            title: 'Change Account Permissions',
            message: `Are you sure you want to demote ${user.username} to a Standard User? This will move them out of the Admin bypass list.`,
            confirmText: 'Proceed',
            type: 'warning',
            onConfirm: async () => {
                try {
                    const response = await adminService.updateRole(user.id, newRole);
                    if (response.success) {
                        toast.success(response.message || `Role updated successfully`);
                        // Update local state
                        setUsers(prev => prev.map(u => u.id === user.id ? { ...u, role: newRole } : u));
                        if (selectedUser && selectedUser.id === user.id) {
                            setSelectedUser(prev => prev ? { ...prev, role: newRole } : null);
                        }
                    }
                } catch (err: any) {
                    toast.error(err.response?.data?.message || "Failed to update role");
                } finally {
                    setConfirmModal(prev => ({ ...prev, isOpen: false }));
                }
            }
        });
    };

    const viewUserDetails = async (user: UserWithStats, defaultTab: 'projects' | 'tokens' = 'projects') => {
        setSelectedUser(user);
        setLoadingDetails(true);
        setLoadingUserTokens(true);
        setUserDetails(null);
        setUserTokenUsage([]);
        setSelectedTab(defaultTab); // Set to requested tab

        try {
            // Fetch user projects
            const response = await adminService.getUserById(user.id);
            if (response.success && response.data) {
                setUserDetails({ projects: response.data.projects });
            }
        } catch (err: any) {
            toast.error("Failed to load user projects");
        } finally {
            setLoadingDetails(false);
        }

        try {
            // Fetch user token usage
            const tokenResponse = await adminService.getTokenUsageData(1, 50, user.id);
            if (tokenResponse.success && tokenResponse.data) {
                setUserTokenUsage(tokenResponse.data.data);
            }
        } catch (err: any) {
            console.error("Failed to load user token usage:", err);
        } finally {
            setLoadingUserTokens(false);
        }
    };

    const getSubscriptionBadge = (subscribed: 'F' | 'P' | 'S') => {
        const badges = {
            F: { text: 'Free', className: 'bg-gray-100 text-gray-700' },
            P: { text: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
            S: { text: 'Subscribed', className: 'bg-green-100 text-green-700' },
        };
        const badge = badges[subscribed];
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.className}`}>
                {badge.text}
            </span>
        );
    };


    const allFiltered = users.filter(u => {
        const matchesSearch =
            (u.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (u.username || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (u.companyName || '').toLowerCase().includes(searchQuery.toLowerCase());

        const matchesSubscription = filterSubscription === 'all' || u.subscribed === filterSubscription;

        return matchesSearch && matchesSubscription;
    });

    const platformUsers = allFiltered.filter(u => u.role === 'user');

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen relative overflow-hidden bg-[#0B0F19]">
                <div className="absolute inset-0 bg-primary/5 blur-[120px] rounded-full animate-pulse-subtle"></div>
                <div className="text-center relative z-10 w-full flex flex-col items-center">
                    <Activity className="h-10 w-10 animate-spin text-primary mb-4" />
                    <p className="text-primary font-black uppercase tracking-widest text-xs">Accessing Mainframe...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen relative overflow-hidden bg-[#0B0F19]">
                <div className="absolute inset-0 bg-red-500/5 blur-[120px] rounded-full"></div>
                <div className="text-center p-12 glass-panel rounded-2xl max-w-md relative z-10 border border-red-500/20">
                    <div className="bg-red-500/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 border border-red-500/30">
                        <UserX className="w-10 h-10 text-red-500 drop-shadow-[0_0_15px_rgba(239,68,68,0.6)]" />
                    </div>
                    <h2 className="text-2xl font-black text-white mb-3 tracking-wide">ACCESS DENIED</h2>
                    <p className="text-text-secondary mb-8 font-medium">{error}</p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="px-8 py-3 bg-red-600/20 text-red-500 rounded-xl hover:bg-red-600/30 border border-red-500/30 transition-all font-black uppercase tracking-widest shadow-[0_0_20px_rgba(239,68,68,0.2)]"
                    >
                        ABORT TO DASHBOARD
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen relative overflow-hidden bg-[#0B0F19] py-8 px-4 sm:px-6 lg:px-8">
            <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5"></div>
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8 relative z-10 w-full max-w-[1600px] mx-auto">
                {/* Header */}
                <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="bg-primary/20 p-4 rounded-xl shadow-[0_0_20px_rgba(255,107,0,0.3)] border border-primary/30 relative">
                            <div className="absolute inset-0 bg-primary/40 blur-md rounded-xl animate-pulse"></div>
                            <Shield className="w-8 h-8 text-primary relative z-10" />
                        </div>
                        <div>
                            <h1 className="text-4xl font-black text-white tracking-widest uppercase">
                                Root Access
                            </h1>
                            <p className="text-text-secondary mt-1 font-medium font-mono text-sm tracking-tight border-l-2 border-primary/50 pl-3">SYSTEM ADMINISTRATOR CONSOLE</p>
                        </div>
                    </div>
                    <button
                        onClick={fetchData}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 glass-panel border border-primary/30 rounded-xl hover:bg-primary/10 hover:border-primary/50 hover:shadow-[0_0_15px_rgba(255,107,0,0.2)] transition-all text-[11px] font-black uppercase tracking-widest text-primary group"
                    >
                        <Activity className="w-4 h-4 text-primary group-hover:animate-spin" /> SYNC DATA
                    </button>
                </div>

                {/* Dashboard Stats Cards */}
                {dashboardStats && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {/* Users Stats */}
                        <div className="glass-panel rounded-2xl p-6 border border-white/5 hover:border-primary/30 transition-all group overflow-hidden relative">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-[40px] -mr-10 -mt-10 pointer-events-none"></div>
                            <div className="flex items-center justify-between mb-4 relative z-10">
                                <div className="bg-blue-500/10 p-3 rounded-xl border border-blue-500/20 group-hover:bg-blue-500/20 transition-colors">
                                    <Users className="w-6 h-6 text-blue-400" />
                                </div>
                                <TrendingUp className="w-5 h-5 text-blue-400 opacity-60" />
                            </div>
                            <h3 className="text-text-secondary/60 text-[10px] uppercase font-black tracking-widest mb-1 relative z-10">Total Entities</h3>
                            <p className="text-3xl font-black text-white mb-3 relative z-10">{dashboardStats.users.total}</p>
                            <div className="flex gap-4 text-xs text-text-secondary/80 font-bold bg-black/20 p-2 rounded-lg border border-white/5 relative z-10">
                                <span className="flex items-center gap-1.5 text-blue-400">
                                    <UserCheck className="w-3.5 h-3.5" /> {dashboardStats.users.active} Active
                                </span>
                                <span className="flex items-center gap-1.5 text-purple-400">
                                    <Crown className="w-3.5 h-3.5" /> {dashboardStats.users.admins} Admins
                                </span>
                            </div>
                        </div>

                        {/* Subscription Stats */}
                        <div className="glass-panel rounded-2xl p-6 border border-white/5 hover:border-primary/30 transition-all group overflow-hidden relative">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-[40px] -mr-10 -mt-10 pointer-events-none"></div>
                            <div className="flex items-center justify-between mb-4 relative z-10">
                                <div className="bg-emerald-500/10 p-3 rounded-xl border border-emerald-500/20 group-hover:bg-emerald-500/20 transition-colors">
                                    <CreditCard className="w-6 h-6 text-emerald-400" />
                                </div>
                                <Activity className="w-5 h-5 text-emerald-400 opacity-60" />
                            </div>
                            <h3 className="text-text-secondary/60 text-[10px] uppercase font-black tracking-widest mb-1 relative z-10">Pro Subscriptions</h3>
                            <p className="text-3xl font-black text-white mb-3 relative z-10">{dashboardStats.users.subscribed}</p>
                            <div className="text-xs text-text-secondary/80 font-bold bg-black/20 p-2 rounded-lg border border-white/5 relative z-10">
                                <span>{dashboardStats.users.free} Starter Tier</span>
                            </div>
                        </div>

                        {/* Projects Stats */}
                        <div className="glass-panel rounded-2xl p-6 border border-white/5 hover:border-primary/30 transition-all group overflow-hidden relative">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-[40px] -mr-10 -mt-10 pointer-events-none"></div>
                            <div className="flex items-center justify-between mb-4 relative z-10">
                                <div className="bg-primary/10 p-3 rounded-xl border border-primary/20 group-hover:bg-primary/20 transition-colors">
                                    <Folder className="w-6 h-6 text-primary" />
                                </div>
                                <Activity className="w-5 h-5 text-primary opacity-60 animate-pulse-subtle" />
                            </div>
                            <h3 className="text-text-secondary/60 text-[10px] uppercase font-black tracking-widest mb-1 relative z-10">Active Matrices</h3>
                            <p className="text-3xl font-black text-white mb-3 relative z-10">{dashboardStats.projects.total}</p>
                            <div className="flex gap-4 text-xs text-text-secondary/80 font-bold bg-black/20 p-2 rounded-lg border border-white/5 relative z-10">
                                <span className="flex items-center gap-1.5 text-primary">
                                    <Clock className="w-3.5 h-3.5" /> {dashboardStats.projects.active} Open
                                </span>
                                <span className="flex items-center gap-1.5 text-emerald-400">
                                    <CheckCircle className="w-3.5 h-3.5" /> {dashboardStats.projects.completed} Done
                                </span>
                            </div>
                        </div>

                        {/* Content Stats */}
                        <div className="glass-panel rounded-2xl p-6 border border-white/5 hover:border-primary/30 transition-all group overflow-hidden relative">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-[40px] -mr-10 -mt-10 pointer-events-none"></div>
                            <div className="flex items-center justify-between mb-4 relative z-10">
                                <div className="bg-indigo-500/10 p-3 rounded-xl border border-indigo-500/20 group-hover:bg-indigo-500/20 transition-colors">
                                    <FileText className="w-6 h-6 text-indigo-400" />
                                </div>
                                <Activity className="w-5 h-5 text-indigo-400 opacity-60" />
                            </div>
                            <h3 className="text-text-secondary/60 text-[10px] uppercase font-black tracking-widest mb-1 relative z-10">Total Generation</h3>
                            <p className="text-3xl font-black text-white mb-3 relative z-10">
                                {dashboardStats.content.requirements +
                                    dashboardStats.content.userStories +
                                    dashboardStats.content.testCases +
                                    dashboardStats.content.tasks}
                            </p>
                            <div className="grid grid-cols-2 gap-2 text-[10px] text-text-secondary/80 font-black uppercase bg-black/20 p-2 rounded-lg border border-white/5 relative z-10">
                                <span>{dashboardStats.content.requirements} Reqs</span>
                                <span>{dashboardStats.content.userStories} Story</span>
                                <span>{dashboardStats.content.testCases} Tests</span>
                                <span>{dashboardStats.content.tasks} Tasks</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Console Sections Switcher */}
                <div className="flex border-b border-white/10 gap-1 mb-6">
                    <button
                        onClick={() => setActiveSection('users')}
                        className={`px-5 py-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all ${
                            activeSection === 'users'
                                ? 'border-primary text-primary bg-primary/5'
                                : 'border-transparent text-text-secondary hover:text-white'
                        }`}
                    >
                        User Management
                    </button>
                    <button
                        onClick={() => setActiveSection('coupons')}
                        className={`px-5 py-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all ${
                            activeSection === 'coupons'
                                ? 'border-primary text-primary bg-primary/5'
                                : 'border-transparent text-text-secondary hover:text-white'
                        }`}
                    >
                        Coupon Codes
                    </button>
                    <button
                        onClick={() => setActiveSection('routing')}
                        className={`px-5 py-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all flex items-center gap-2 ${
                            activeSection === 'routing'
                                ? 'border-primary text-primary bg-primary/5'
                                : 'border-transparent text-text-secondary hover:text-white'
                        }`}
                    >
                        <Activity className="w-4 h-4" />
                        Model Routing
                    </button>
                </div>

                {activeSection === 'users' && (
                    <>
                        {/* Filters and Search */}
                <div className="glass-panel rounded-2xl p-6 mb-6 border border-white/5 overflow-visible relative z-20">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Search */}
                        <div className="md:col-span-2">
                            <label className="block text-[11px] font-black text-text-secondary uppercase tracking-widest mb-2">Query Entities</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary/50" />
                                <input
                                    type="text"
                                    placeholder="Search by signature, email, or organization..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-11 pr-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all text-sm font-medium text-white placeholder-text-secondary/30 shadow-inner"
                                />
                            </div>
                        </div>

                        {/* Subscription Filter */}
                        <div>
                            <label className="block text-[11px] font-black text-text-secondary uppercase tracking-widest mb-2">Class Filter</label>
                            <select
                                value={filterSubscription}
                                onChange={(e) => setFilterSubscription(e.target.value as any)}
                                className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all text-sm font-bold text-white shadow-inner appearance-none custom-select"
                            >
                                <option value="all">All Classes</option>
                                <option value="F">Free/Starter</option>
                                <option value="P">Pending Setup</option>
                                <option value="S">Pro Subscription</option>
                            </select>
                        </div>
                    </div>
                </div>


                {/* Users Table */}
                <div className="glass-panel rounded-2xl overflow-hidden border border-white/5 mb-12 shadow-2xl relative z-10 w-full pl-0">
                    <div className="px-6 py-5 bg-black/40 border-b border-white/10 flex justify-between items-center relative overflow-hidden backdrop-blur-xl">
                        <div className="absolute inset-0 bg-primary/10 blur-[50px] mix-blend-screen opacity-20 pointer-events-none"></div>
                        <h2 className="text-xl font-black text-white flex items-center gap-3 uppercase tracking-wide relative z-10">
                            <Users className="w-6 h-6 text-primary" />
                            Entity Roster <span className="bg-primary/20 text-primary border border-primary/30 px-2 py-0.5 rounded-lg text-sm">{platformUsers.length}</span>
                        </h2>
                    </div>

                    <div className="overflow-x-auto custom-scrollbar">
                        <table className="w-full whitespace-nowrap min-w-full">
                            <thead className="bg-white/5 border-b border-white/10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-[11px] font-black text-text-secondary uppercase tracking-widest min-w-[200px]">Identity Handle</th>
                                    <th className="px-6 py-4 text-left text-[11px] font-black text-text-secondary uppercase tracking-widest min-w-[120px]">Class</th>
                                    <th className="px-6 py-4 text-left text-[11px] font-black text-text-secondary uppercase tracking-widest text-center min-w-[100px]">Matrices</th>
                                    <th className="px-6 py-4 text-left text-[11px] font-black text-text-secondary uppercase tracking-widest min-w-[180px]">Computation Load</th>
                                    <th className="px-6 py-4 text-center text-[11px] font-black text-text-secondary uppercase tracking-widest min-w-[150px]">Authorization</th>
                                    <th className="px-6 py-4 text-right text-[11px] font-black text-text-secondary uppercase tracking-widest min-w-[120px]">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5 bg-black/20">
                                {platformUsers.map((user) => (
                                    <tr key={user.id} className={`hover:bg-white/5 transition-colors group ${!user.isActive ? 'bg-red-500/5 opacity-80' : ''}`}>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-4">
                                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-black text-white shadow-inner transition-transform group-hover:scale-105 border ${user.isActive ? 'bg-primary/20 border-primary/30 text-primary uppercase' : 'bg-white/10 border-white/20 text-text-secondary'}`}>
                                                    {user.username.charAt(0)}
                                                </div>
                                                <div className="min-w-0 pr-4">
                                                    <div className="font-bold text-white text-sm uppercase tracking-wide truncate">{user.username}</div>
                                                    <div className="text-xs text-text-secondary font-medium mt-0.5 truncate max-w-[200px]" title={user.email}>{user.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            {getSubscriptionBadge(user.subscribed)}
                                        </td>
                                        <td className="px-6 py-5 text-center">
                                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-xl shadow-inner">
                                                <span className="text-white font-black">{user.statistics.totalProjects}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex flex-col gap-3 min-w-[160px]">
                                                {/* Daily Limit */}
                                                <div className="w-full">
                                                    <div className="flex justify-between text-[10px] mb-1">
                                                        <span className="font-bold text-text-secondary/70 uppercase tracking-tighter">Daily Burst</span>
                                                        <span className={`font-black ${user.statistics.dailyLimit && user.statistics.dailyUsed >= user.statistics.dailyLimit ? 'text-red-500' : 'text-primary'}`}>
                                                            {Math.round(user.statistics.dailyUsed / 100).toLocaleString()}{user.statistics.dailyLimit ? <span className="text-text-secondary font-medium"> / {Math.round(user.statistics.dailyLimit / 100).toLocaleString()}</span> : ' / ∞'}
                                                        </span>
                                                    </div>
                                                    <div className="w-full bg-black/60 rounded-full h-1.5 overflow-hidden shadow-inner border border-white/5">
                                                        <div
                                                            className={`h-full rounded-full transition-all duration-500 ${user.statistics.dailyLimit && (user.statistics.dailyUsed / user.statistics.dailyLimit) > 0.8
                                                                ? 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.8)]' : 'bg-primary shadow-[0_0_10px_rgba(255,107,0,0.8)]'
                                                                }`}
                                                            style={{ width: `${user.statistics.dailyLimit ? Math.min(100, (user.statistics.dailyUsed / user.statistics.dailyLimit) * 100) : 0}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                                {/* Total Pool */}
                                                <div className="w-full">
                                                    <div className="flex justify-between text-[10px] mb-1">
                                                        <span className="font-bold text-text-secondary/70 uppercase tracking-tighter">Global Pool</span>
                                                        <span className="text-info font-black">{Math.round(user.statistics.remainingTokens / 100).toLocaleString()} Left</span>
                                                    </div>
                                                    <div className="w-full bg-black/60 rounded-full h-1 overflow-hidden border border-white/5">
                                                        <div
                                                            className="h-full bg-info rounded-full transition-all shadow-[0_0_8px_rgba(59,130,246,0.8)]"
                                                            style={{ width: `${Math.min(100, (user.statistics.remainingTokens / (user.statistics.totalAllowance || 1)) * 100)}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center justify-center gap-3">
                                                {/* Disable/Enable Option */}
                                                <button
                                                    onClick={() => handleToggleStatus(user)}
                                                    className={`px-4 py-1.5 rounded-xl text-[10px] uppercase tracking-widest font-black transition-all flex items-center gap-2 border ${user.isActive
                                                        ? 'bg-red-500/10 text-red-500 border-red-500/30 hover:bg-red-500/20 hover:shadow-[0_0_15px_rgba(239,68,68,0.2)]'
                                                        : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/20 hover:shadow-[0_0_15px_rgba(16,185,129,0.2)]'
                                                        }`}
                                                >
                                                    <Power className="w-3.5 h-3.5" />
                                                    {user.isActive ? "REVOKE" : "GRANT"}
                                                </button>

                                                {/* Plan Selection */}
                                                <select
                                                    value={user.planType}
                                                    onChange={(e) => handleUpdatePlan(user, e.target.value as any)}
                                                    className="bg-purple-500/10 text-purple-400 border border-purple-500/30 rounded-xl px-2 py-1 text-[10px] font-black uppercase tracking-widest hover:bg-purple-500/20 transition-all outline-none"
                                                >
                                                    <option value="free" className="bg-[#0B0F19]">Free</option>
                                                    <option value="starter" className="bg-[#0B0F19]">Starter</option>
                                                    <option value="intelligence" className="bg-[#0B0F19]">Intelligence</option>
                                                </select>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => viewUserTokenUsage(user)}
                                                    className="px-3 py-1.5 text-info hover:text-white bg-info/10 border border-info/30 hover:bg-info/20 rounded-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 whitespace-nowrap"
                                                    title="View compute logs"
                                                >
                                                    <Activity className="w-3.5 h-3.5" /> LOGS
                                                </button>
                                                <button
                                                    onClick={() => viewUserDetails(user)}
                                                    className="p-1.5 text-text-secondary hover:text-white hover:bg-white/10 border border-transparent hover:border-white/10 rounded-xl transition-all"
                                                >
                                                    <ChevronRight className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {platformUsers.length === 0 && (
                                    <tr className="text-center bg-black/40">
                                        <td colSpan={6} className="py-24">
                                            <div className="bg-white/5 w-16 h-16 rounded-2xl border border-white/10 flex items-center justify-center mx-auto mb-4 shadow-inner">
                                                <UserX className="w-8 h-8 text-text-secondary/50" />
                                            </div>
                                            <p className="text-text-secondary font-black uppercase tracking-widest">No entities intersect query parameters</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </>)}

                {activeSection === 'coupons' && (
                    <div className="space-y-6">
                        {/* Coupon Manager Header with Add Button */}
                        <div className="flex justify-between items-center bg-[#131B2C]/40 px-6 py-5 rounded-2xl border border-white/5 relative overflow-hidden backdrop-blur-xl">
                            <div className="absolute inset-0 bg-primary/10 blur-[50px] mix-blend-screen opacity-20 pointer-events-none"></div>
                            <h2 className="text-xl font-black text-white flex items-center gap-3 uppercase tracking-wide relative z-10">
                                <CreditCard className="w-6 h-6 text-primary" />
                                Coupon Codes <span className="bg-primary/20 text-primary border border-primary/30 px-2 py-0.5 rounded-lg text-sm">{coupons.length}</span>
                            </h2>
                            <button
                                onClick={() => {
                                    setCouponForm({ id: null, couponName: '', discountPercent: 50, redeemsCount: 1, planType: 'starter', isActive: true });
                                    setIsCouponModalOpen(true);
                                }}
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary to-orange-600 rounded-xl hover:brightness-110 shadow-lg transition-all text-xs font-black uppercase tracking-wider text-white border-none"
                            >
                                Create Coupon
                            </button>
                        </div>
                        

                        {/* Coupons Table */}
                        <div className="glass-panel rounded-2xl overflow-hidden border border-white/5 shadow-2xl relative z-10 w-full">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">                                     <thead className="bg-white/5 border-b border-white/10">
                                        <tr className="text-[11px] font-black uppercase tracking-widest text-text-secondary">
                                            <th className="py-4 px-6">Coupon Name</th>
                                            <th className="py-4 px-6">Target Plan</th>
                                            <th className="py-4 px-6">Discount</th>
                                            <th className="py-4 px-6">Redemption Limit</th>
                                            <th className="py-4 px-6">Status</th>
                                            <th className="py-4 px-6 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5 bg-black/20 text-xs">
                                        {coupons.length > 0 ? (
                                            coupons.map((coupon) => (
                                                <tr key={coupon.id} className="text-white hover:bg-white/[0.02] transition-colors animate-in fade-in duration-300">
                                                    <td className="py-4 px-6 font-black tracking-wider text-sm text-primary uppercase">{coupon.couponName}</td>
                                                    <td className="py-4 px-6">
                                                        <span className={`px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-widest ${
                                                            coupon.planType === 'intelligence' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                                                        }`}>
                                                            {coupon.planType === 'intelligence' ? 'Elite' : 'Growth'}
                                                        </span>
                                                    </td>                                                    <td className="py-4 px-6 font-bold text-white text-sm">{coupon.discountPercent}% OFF</td>
                                                    <td className="py-4 px-6 font-medium text-text-secondary">{coupon.redeemsCount} uses per account</td>
                                                    <td className="py-4 px-6">
                                                        <button
                                                            onClick={() => handleToggleCouponActive(coupon)}
                                                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border transition-all ${
                                                                coupon.isActive
                                                                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20'
                                                                    : 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20'
                                                            }`}
                                                        >
                                                            {coupon.isActive ? 'Active' : 'Inactive'}
                                                        </button>
                                                    </td>
                                                    <td className="py-4 px-6 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <button
                                                                onClick={() => {
                                                                    setCouponForm({
                                                                        id: coupon.id,
                                                                        couponName: coupon.couponName,
                                                                        discountPercent: coupon.discountPercent,
                                                                        redeemsCount: coupon.redeemsCount,
                                                                        planType: coupon.planType || 'starter',
                                                                        isActive: coupon.isActive
                                                                    });
                                                                    setIsCouponModalOpen(true);
                                                                }}
                                                                className="px-3 py-1.5 bg-white/5 border border-white/10 hover:border-primary/30 hover:bg-primary/10 rounded-xl transition-all text-[10px] font-black uppercase tracking-wider text-text-secondary hover:text-white"
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteCoupon(coupon)}
                                                                className="px-3 py-1.5 bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 rounded-xl transition-all text-[10px] font-black uppercase tracking-wider text-red-400"
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="py-12 text-center text-text-secondary font-bold uppercase tracking-wider">
                                                    No coupon codes created
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {activeSection === 'routing' && (
                    <div className="space-y-6">
                        {/* Provider Tabs */}
                        <div className="flex border-b border-white/10 gap-1 mb-6">
                            <button
                                onClick={() => setRoutingProviderFilter('all')}
                                className={`px-5 py-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all ${
                                    routingProviderFilter === 'all'
                                        ? 'border-primary text-primary bg-primary/5'
                                        : 'border-transparent text-text-secondary hover:text-white'
                                }`}
                            >
                                All Providers
                            </button>
                            <button
                                onClick={() => setRoutingProviderFilter('Gemini')}
                                className={`px-5 py-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all flex items-center gap-2 ${
                                    routingProviderFilter === 'Gemini'
                                        ? 'border-blue-400 text-blue-400 bg-blue-500/5'
                                        : 'border-transparent text-text-secondary hover:text-white'
                                }`}
                            >
                                Gemini Models
                            </button>
                            <button
                                onClick={() => setRoutingProviderFilter('OpenRouter')}
                                className={`px-5 py-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all flex items-center gap-2 ${
                                    routingProviderFilter === 'OpenRouter'
                                        ? 'border-purple-400 text-purple-400 bg-purple-500/5'
                                        : 'border-transparent text-text-secondary hover:text-white'
                                }`}
                            >
                                OpenRouter Models
                            </button>
                        </div>

                        {/* Model Routing Matrix */}
                        <div className="glass-panel rounded-2xl overflow-hidden border border-white/5 mb-12 shadow-2xl relative z-10 w-full pl-0">
                            <div className="px-6 py-5 bg-black/40 border-b border-white/10 flex justify-between items-center relative overflow-hidden backdrop-blur-xl">
                                <div className="absolute inset-0 bg-primary/10 blur-[50px] mix-blend-screen opacity-20 pointer-events-none"></div>
                                <h2 className="text-xl font-black text-white flex items-center gap-3 uppercase tracking-wide relative z-10">
                                    <Activity className="w-6 h-6 text-primary" />
                                    AI Model Routing Matrix
                                </h2>
                            </div>

                            <div className="overflow-x-auto custom-scrollbar">
                                <table className="w-full whitespace-nowrap min-w-full">
                                    <thead className="bg-white/5 border-b border-white/10">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-[11px] font-black text-text-secondary uppercase tracking-widest">Provider</th>
                                            <th className="px-6 py-4 text-left text-[11px] font-black text-text-secondary uppercase tracking-widest">Task Priority</th>
                                            <th className="px-6 py-4 text-left text-[11px] font-black text-text-secondary uppercase tracking-widest">Top 1 Model (Primary)</th>
                                            <th className="px-6 py-4 text-left text-[11px] font-black text-text-secondary uppercase tracking-widest">Fallback Models (Secondary)</th>
                                            <th className="px-6 py-4 text-left text-[11px] font-black text-text-secondary uppercase tracking-widest">Score (Priority)</th>
                                            <th className="px-6 py-4 text-right text-[11px] font-black text-text-secondary uppercase tracking-widest">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5 bg-black/20">
                                        {modelRouting
                                            .filter(r => {
                                                const p = r.provider || (r.task_name.includes(':') ? r.task_name.split(':')[1] : 'OpenRouter');
                                                return routingProviderFilter === 'all' || p === routingProviderFilter;
                                            })
                                            .map((route, i) => {
                                                const isGlobal = route.task_name.startsWith('GLOBAL_FALLBACK');
                                                const taskBase = route.task_name.split(':')[0];
                                                const providerName = route.provider || (route.task_name.includes(':') ? route.task_name.split(':')[1] : 'OpenRouter');

                                                return (
                                                    <tr key={i} className={`hover:bg-white/5 transition-colors group ${isGlobal ? 'bg-primary/5' : ''}`}>
                                                        <td className="px-6 py-5">
                                                            <div className="flex flex-col gap-1">
                                                                <span className={`px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-widest inline-block w-fit ${
                                                                    providerName === 'Gemini' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                                                                }`}>
                                                                    {providerName}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-5 font-bold text-white text-sm">
                                                            {isGlobal ? (
                                                                <span className="text-primary tracking-widest">GLOBAL FALLBACK</span>
                                                            ) : (
                                                                <>
                                                                    {taskBase}
                                                                </>
                                                            )}
                                                            <div className="text-[9px] text-text-secondary mt-1 font-mono">
                                                                {isGlobal ? 'ALL TASKS' : 'TASK SPECIFIC'}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-5">
                                                            <span className="px-2.5 py-1 bg-primary/10 text-primary border border-primary/20 rounded-lg text-sm font-medium">
                                                                {route.model_id}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-5">
                                                            <div className="flex flex-wrap gap-1.5 max-w-sm">
                                                                {route.fallback_models ? route.fallback_models.split(',').map((fm: string, idx: number) => (
                                                                    <span key={idx} className="px-2 py-0.5 bg-white/5 border border-white/10 rounded text-[10px] text-text-secondary hover:text-white transition-colors">
                                                                        {idx + 1}. {fm.trim()}
                                                                    </span>
                                                                )) : (
                                                                    <span className="text-[10px] text-text-secondary/50 italic">No fallbacks configured</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-5">
                                                            <div className="flex flex-col gap-1">
                                                                <div className="w-16 h-1.5 bg-black/40 rounded-full overflow-hidden border border-white/5">
                                                                    <div 
                                                                        className="h-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" 
                                                                        style={{ width: `${Math.min(100, (Number(route.score) || 0) * 10)}%` }}
                                                                    ></div>
                                                                </div>
                                                                <span className="text-xs font-bold text-emerald-400">Score: {Number(route.score || 0).toFixed(1)}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-5 text-right">
                                                            <button
                                                                onClick={() => {
                                                                    setRoutingForm({
                                                                        id: route.id,
                                                                        task_name: route.task_name,
                                                                        model_id: route.model_id || '',
                                                                        fallback_models: route.fallback_models || '',
                                                                        score: Number(route.score) || 0
                                                                    });
                                                                    setIsRoutingModalOpen(true);
                                                                }}
                                                                className="px-3 py-1.5 text-primary hover:text-white bg-primary/10 border border-primary/30 hover:bg-primary/20 rounded-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-end gap-1.5 ml-auto"
                                                            >
                                                                <ExternalLink className="w-3.5 h-3.5" /> Configure
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        {modelRouting.length === 0 && (
                                            <tr className="text-center bg-black/40">
                                                <td colSpan={6} className="py-12 text-text-secondary font-black uppercase tracking-widest">
                                                    No routing data available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* DETAILS MODAL */}
            {selectedUser && (
                <div className="fixed inset-0 z-[60] overflow-y-auto overflow-x-hidden flex items-center justify-center p-4 sm:p-6">
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-md transition-opacity" onClick={() => setSelectedUser(null)}></div>

                    <div className="relative glass-panel rounded-3xl shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/10 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col transform transition-all animate-in zoom-in-95 duration-300">
                        {/* Modal Header */}
                        <div className="bg-black/60 px-8 py-6 flex justify-between items-center text-white shrink-0 border-b border-white/10 relative overflow-hidden">
                            <div className="absolute inset-0 bg-primary/10 blur-[40px] pointer-events-none"></div>
                            <div className="flex items-center gap-4 relative z-10">
                                <div className="w-14 h-14 rounded-2xl bg-primary/20 backdrop-blur-md flex items-center justify-center font-black text-2xl shadow-inner border border-primary/30 text-primary uppercase">
                                    {selectedUser.username.charAt(0)}
                                </div>
                                <div>
                                    <h2 className="text-2xl font-black tracking-wide flex items-center gap-2 uppercase">
                                        {selectedUser.username}
                                        <span className="text-[10px] font-black opacity-80 px-2 py-0.5 rounded-md bg-black/40 border border-white/10 tracking-widest text-primary">
                                            {selectedUser.role}
                                        </span>
                                    </h2>
                                    <p className="text-text-secondary flex items-center gap-2 mt-1 text-sm font-medium font-mono">
                                        <Activity className={`w-3.5 h-3.5 ${selectedUser.isActive ? 'text-primary animate-pulse-subtle' : 'text-red-500'}`} />
                                        {selectedUser.isActive ? 'SYSTEM ONLINE' : 'SYSTEM OFFLINE'} <span className="opacity-40">|</span> INIT DATE: {new Date(selectedUser.createdAt).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedUser(null)}
                                className="p-2 rounded-xl hover:bg-white/10 border border-transparent hover:border-white/10 transition-colors relative z-10 text-text-secondary hover:text-white"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-black/20">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                {/* Left Column: Info & Stats */}
                                <div className="lg:col-span-1 space-y-6">
                                    <section className="glass-panel bg-black/40 rounded-2xl p-6 border border-white/5 relative overflow-hidden">
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-full blur-[30px] -mr-5 -mt-5"></div>
                                        <h3 className="text-[10px] font-black text-primary uppercase tracking-widest mb-5 flex items-center gap-2 relative z-10">
                                            <UserCheck className="w-4 h-4" /> Identity Matrix
                                        </h3>
                                        <div className="space-y-5 relative z-10">
                                            <div className="flex items-start gap-4">
                                                <div className="p-2 bg-white/5 rounded-lg border border-white/5 shrink-0 mt-0.5">
                                                    <FileText className="w-4 h-4 text-text-secondary" />
                                                </div>
                                                <div>
                                                    <p className="text-[9px] text-text-secondary/70 uppercase font-black tracking-widest mb-1 mt-0.5">Assigned Name</p>
                                                    <p className="text-sm font-bold text-white uppercase tracking-wide">{selectedUser.firstName} {selectedUser.lastName}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-4">
                                                <div className="p-2 bg-white/5 rounded-lg border border-white/5 shrink-0 mt-0.5">
                                                    <Building2 className="w-4 h-4 text-text-secondary" />
                                                </div>
                                                <div>
                                                    <p className="text-[9px] text-text-secondary/70 uppercase font-black tracking-widest mb-1 mt-0.5">Organization</p>
                                                    <p className="text-sm font-bold text-white uppercase tracking-wide">{selectedUser.companyName}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-4">
                                                <div className="p-2 bg-white/5 rounded-lg border border-white/5 shrink-0 mt-0.5">
                                                    <Activity className="w-4 h-4 text-text-secondary" />
                                                </div>
                                                <div>
                                                    <p className="text-[9px] text-text-secondary/70 uppercase font-black tracking-widest mb-1 mt-0.5">Plan Tier</p>
                                                    <p className="text-sm font-bold text-white break-all">{selectedUser.email}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </section>

                                    <section className="glass-panel bg-black/40 rounded-2xl p-6 border border-white/5">
                                        <h3 className="text-[10px] font-black text-primary uppercase tracking-widest mb-5">Engagement Metrics</h3>
                                        <div className="grid grid-cols-2 gap-4 mb-5">
                                            <div className="bg-white/5 border border-white/10 p-4 rounded-xl text-center relative overflow-hidden group hover:border-primary/30 transition-colors">
                                                <div className="absolute inset-x-0 bottom-0 h-0.5 bg-primary transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                                                <p className="text-3xl font-black text-white">{selectedUser.statistics.totalProjects}</p>
                                                <p className="text-[9px] text-text-secondary font-black uppercase tracking-widest mt-1">Matrices</p>
                                            </div>
                                            <div className="bg-white/5 border border-white/10 p-4 rounded-xl text-center relative overflow-hidden group hover:border-info/30 transition-colors">
                                                <div className="absolute inset-x-0 bottom-0 h-0.5 bg-info transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                                                <p className="text-3xl font-black text-white">{selectedUser.statistics.totalRequirements}</p>
                                                <p className="text-[9px] text-text-secondary font-black uppercase tracking-widest mt-1">Reqs</p>
                                            </div>
                                        </div>

                                        <div className="pt-5 border-t border-white/10">
                                            <h4 className="text-[9px] font-black text-text-secondary/70 uppercase tracking-widest mb-4">Compute Allocation</h4>
                                            <div className="space-y-6">
                                                {/* Daily Cap */}
                                                <div>
                                                    <div className="flex justify-between text-[10px] mb-2 font-mono">
                                                        <span className="text-text-secondary font-bold uppercase tracking-tight">Daily Burst</span>
                                                        <span className={`font-black ${selectedUser.statistics.dailyLimit && selectedUser.statistics.dailyUsed >= selectedUser.statistics.dailyLimit ? 'text-red-500' : 'text-white'}`}>
                                                            {selectedUser.statistics.dailyUsed.toLocaleString()} <span className="text-text-secondary">/ {selectedUser.statistics.dailyLimit ? selectedUser.statistics.dailyLimit.toLocaleString() : '∞'}</span>
                                                        </span>
                                                    </div>
                                                    <div className="w-full bg-black/60 rounded-full h-2 overflow-hidden border border-white/5">
                                                        <div
                                                            className={`h-full rounded-full transition-all duration-700 ${selectedUser.statistics.dailyLimit && (selectedUser.statistics.dailyUsed / selectedUser.statistics.dailyLimit) > 0.9 ? 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.8)]' : 'bg-primary shadow-[0_0_10px_rgba(255,107,0,0.8)]'}`}
                                                            style={{ width: `${selectedUser.statistics.dailyLimit ? Math.min(100, (selectedUser.statistics.dailyUsed / selectedUser.statistics.dailyLimit) * 100) : 0}%` }}
                                                        ></div>
                                                    </div>
                                                    {selectedUser.statistics.dailyLimit && selectedUser.statistics.dailyUsed >= selectedUser.statistics.dailyLimit && (
                                                        <p className="text-[10px] text-red-500 font-bold mt-2 animate-pulse uppercase tracking-widest text-right">!! BURST LIMIT REACHED !!</p>
                                                    )}
                                                </div>

                                                {/* Global Pool */}
                                                <div>
                                                    <div className="flex justify-between text-[10px] mb-2 font-mono">
                                                        <span className="text-text-secondary font-bold uppercase tracking-tight">Global Pool</span>
                                                        <span className="text-info font-black">{selectedUser.statistics.remainingTokens.toLocaleString()} Left</span>
                                                    </div>
                                                    <div className="w-full bg-black/60 rounded-full h-2 overflow-hidden border border-white/5">
                                                        <div
                                                            className="h-full bg-info rounded-full shadow-[0_0_10px_rgba(59,130,246,0.8)]"
                                                            style={{ width: `${Math.min(100, (selectedUser.statistics.remainingTokens / (selectedUser.statistics.totalAllowance || 1)) * 100)}%` }}
                                                        ></div>
                                                    </div>
                                                    <p className="text-[9px] text-text-secondary font-bold uppercase tracking-widest text-right mt-2 font-mono">Pool Max: {selectedUser.statistics.totalAllowance.toLocaleString()}</p>
                                                </div>

                                                {/* Model Breakdown */}
                                                {selectedUser.statistics.modelUsage && selectedUser.statistics.modelUsage.length > 0 && (
                                                    <div className="pt-2">
                                                        <p className="text-[9px] text-text-secondary/70 uppercase font-black tracking-widest mb-3">Compute Per Project</p>
                                                        <div className="space-y-2">
                                                            {selectedUser.statistics.modelUsage.map((m, idx) => (
                                                                <div key={idx} className="flex justify-between items-center text-[10px] py-2 px-3 bg-white/5 border border-white/5 rounded-lg group hover:border-primary/30 transition-colors">
                                                                    <span className="font-bold text-text-secondary uppercase tracking-widest group-hover:text-white transition-colors">{m.model.split('/').pop()}</span>
                                                                    <span className="font-black text-primary font-mono">-{m.used.toLocaleString()}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="flex items-center gap-3 px-4 py-3 bg-primary/10 border border-primary/20 rounded-xl text-[10px]">
                                                    <Shield className="w-4 h-4 text-primary shrink-0 animate-pulse-subtle" />
                                                    <span className="text-primary font-bold uppercase tracking-widest">{selectedUser.statistics.tokenLimitLabel}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                </div>

                                {/* Right Column: Tabs */}
                                <div className="lg:col-span-2 space-y-6">
                                    {/* Tab Headers */}
                                    <div className="flex items-center gap-2 border-b border-white/10">
                                        <button
                                            onClick={() => setSelectedTab('projects')}
                                            className={`px-5 py-3 font-black text-[11px] uppercase tracking-widest transition-all flex items-center gap-2 ${selectedTab === 'projects'
                                                ? 'border-b-2 border-primary text-primary bg-primary/5'
                                                : 'text-text-secondary hover:text-white hover:bg-white/5'
                                                }`}
                                        >
                                            <Folder className="w-4 h-4" />
                                            Matrices
                                            {userDetails?.projects && (
                                                <span className="ml-1 px-2 py-0.5 bg-black/40 border border-white/10 rounded-md text-[9px]">
                                                    {userDetails.projects.length}
                                                </span>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => setSelectedTab('tokens')}
                                            className={`px-5 py-3 font-black text-[11px] uppercase tracking-widest transition-all flex items-center gap-2 ${selectedTab === 'tokens'
                                                ? 'border-b-2 border-primary text-primary bg-primary/5'
                                                : 'text-text-secondary hover:text-white hover:bg-white/5'
                                                }`}
                                        >
                                            <Activity className="w-4 h-4" />
                                            Compute Logs
                                            {userTokenUsage.length > 0 && (
                                                <span className="ml-1 px-2 py-0.5 bg-black/40 border border-white/10 rounded-md text-[9px]">
                                                    {userTokenUsage.length}
                                                </span>
                                            )}
                                        </button>
                                    </div>

                                    {/* Tab Content: Projects */}
                                    {selectedTab === 'projects' && (
                                        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                            {loadingDetails ? (
                                                <div className="glass-panel bg-black/40 rounded-2xl p-16 flex flex-col items-center justify-center border border-white/10 relative overflow-hidden">
                                                    <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5"></div>
                                                    <Activity className="w-10 h-10 text-primary animate-spin mb-4 relative z-10" />
                                                    <p className="text-primary font-black uppercase tracking-widest text-[11px] relative z-10">Syncing Matrix Data...</p>
                                                </div>
                                            ) : userDetails?.projects && userDetails.projects.length > 0 ? (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {userDetails.projects.map((project: any) => (
                                                        <div key={project.id} className="glass-panel bg-black/40 border border-white/5 rounded-2xl p-5 hover:border-primary/30 transition-all group relative overflow-hidden">
                                                            <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                                            <div className="flex justify-between items-start mb-3 relative z-10">
                                                                <div className="bg-white/5 p-2 rounded-xl group-hover:bg-primary/20 border border-white/5 group-hover:border-primary/30 transition-colors">
                                                                    <Folder className="w-4 h-4 text-text-secondary group-hover:text-primary transition-colors" />
                                                                </div>
                                                                <span className={`text-[9px] px-2 py-1 rounded-md font-black uppercase tracking-widest border ${project.status === 'active' ? 'bg-primary/10 text-primary border-primary/20' :
                                                                    project.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-white/5 text-text-secondary border-white/10'
                                                                    }`}>
                                                                    {project.status === 'active' ? 'PROCESSING' : project.status === 'completed' ? 'ARCHIVED' : project.status}
                                                                </span>
                                                            </div>
                                                            <h4 className="font-black text-white line-clamp-1 group-hover:text-primary transition-colors uppercase tracking-wide relative z-10"><span className="text-text-secondary mr-2">{'>_'}</span>{project.name}</h4>
                                                            <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between relative z-10">
                                                                <div className="flex items-center gap-2 text-[10px] text-text-secondary font-mono tracking-wider font-bold">
                                                                    <Calendar className="w-3.5 h-3.5" />
                                                                    {new Date(project.created_at).toLocaleDateString()}
                                                                </div>
                                                                <button
                                                                    onClick={() => navigate(`/projects/${project.id}`)}
                                                                    className="opacity-0 group-hover:opacity-100 transition-opacity bg-primary/20 p-2 rounded-xl text-primary border border-primary/30 hover:bg-primary hover:text-white"
                                                                >
                                                                    <ExternalLink className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="glass-panel bg-black/40 rounded-2xl p-16 text-center border border-white/5">
                                                    <div className="w-16 h-16 bg-white/5 border border-white/10 flex items-center justify-center rounded-2xl mx-auto mb-4">
                                                        <Folder className="w-8 h-8 text-text-secondary/50" />
                                                    </div>
                                                    <p className="text-text-secondary font-black uppercase tracking-widest">No matrices allocated</p>
                                                    <p className="text-text-secondary/50 text-xs mt-2 font-medium">Entity has not initialized any workspace operations.</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Tab Content: Token Usage */}
                                    {selectedTab === 'tokens' && (
                                        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                            {loadingUserTokens ? (
                                                <div className="glass-panel bg-black/40 rounded-2xl p-16 flex flex-col items-center justify-center border border-white/10 relative overflow-hidden">
                                                    <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5"></div>
                                                    <Activity className="w-10 h-10 text-primary animate-spin mb-4 relative z-10" />
                                                    <p className="text-primary font-black uppercase tracking-widest text-[11px] relative z-10">Fetching Compute Metrics...</p>
                                                </div>
                                            ) : userTokenUsage.length > 0 ? (
                                                <div className="glass-panel bg-black/40 border border-white/5 rounded-2xl overflow-hidden">
                                                    <table className="w-full text-left whitespace-nowrap min-w-full">
                                                        <thead className="bg-white/5 border-b border-white/10">
                                                            <tr>
                                                                <th className="px-5 py-4 text-[9px] font-black text-text-secondary uppercase tracking-widest min-w-[200px]">User / Plan</th>
                                                                <th className="px-5 py-4 text-[9px] font-black text-text-secondary uppercase tracking-widest text-right min-w-[100px]">Load</th>
                                                                <th className="px-5 py-4 text-[9px] font-black text-text-secondary uppercase tracking-widest text-right min-w-[120px]">Capacity Status</th>
                                                                <th className="px-5 py-4 text-[9px] font-black text-text-secondary uppercase tracking-widest text-right min-w-[140px]">Timestamp</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-white/5">
                                                            {userTokenUsage.map((token) => (
                                                                <tr key={token.id} className="hover:bg-white/5 transition-colors">
                                                                    <td className="px-5 py-4">
                                                                        <div className="flex flex-col">
                                                                            <span className="text-sm font-bold text-white uppercase tracking-wide truncate max-w-[220px]" title={token.projectName}>{token.projectName || 'General AI Chat'}</span>
                                                                            <div className="flex items-center gap-2 mt-1.5">
                                                                                <span className="px-2 py-0.5 bg-primary/10 text-primary border border-primary/20 rounded-md text-[9px] font-black uppercase tracking-widest shrink-0">
                                                                                    {token.documentType}
                                                                                </span>
                                                                                <span className="text-[10px] text-text-secondary/70 font-mono font-bold truncate max-w-[120px]" title={token.modelUsed}>
                                                                                    {token.modelUsed.split('/').pop()}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-5 py-4 text-right">
                                                                        <span className="text-sm font-black text-red-500 font-mono">-{token.tokenCount.toLocaleString()}</span>
                                                                    </td>
                                                                    <td className="px-5 py-4 text-right">
                                                                        <div className="flex flex-col items-end">
                                                                            <span className="text-[11px] font-black text-info font-mono">{(token.modelRemaining || 0).toLocaleString()}</span>
                                                                            <div className="flex items-center gap-1.5 mt-1">
                                                                                <span className="text-[9px] font-black text-text-secondary/50 uppercase tracking-widest">Global:</span>
                                                                                <span className="text-[10px] font-bold text-text-secondary font-mono">{token.remainingTokens.toLocaleString()}</span>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-5 py-4 text-right">
                                                                        <div className="flex flex-col items-end">
                                                                            <span className="text-xs font-bold text-white font-mono">{new Date(token.createdAt).toLocaleDateString()}</span>
                                                                            <span className="text-[10px] text-text-secondary/70 font-mono mt-0.5">{new Date(token.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="glass-panel bg-black/40 rounded-2xl p-16 text-center border border-white/5">
                                                    <div className="w-16 h-16 bg-white/5 border border-white/10 flex items-center justify-center rounded-2xl mx-auto mb-4">
                                                        <Activity className="w-8 h-8 text-text-secondary/50" />
                                                    </div>
                                                    <p className="text-text-secondary font-black uppercase tracking-widest">No data found</p>
                                                    <p className="text-text-secondary/50 text-xs mt-2 font-medium">Entity computation log is completely void.</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="bg-black/60 px-8 py-5 flex justify-between items-center shrink-0 border-t border-white/10">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => handleToggleStatus(selectedUser)}
                                    className={`flex items-center gap-2 px-6 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all glass-panel border ${selectedUser.id === currentUser?.id
                                        ? 'bg-white/5 border-white/10 text-white/20 cursor-not-allowed'
                                        : selectedUser.isActive
                                            ? 'bg-red-500/10 border-red-500/30 text-red-500 hover:bg-red-500/20 hover:shadow-[0_0_15px_rgba(239,68,68,0.2)]'
                                            : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/20 hover:shadow-[0_0_15px_rgba(16,185,129,0.2)]'
                                        }`}
                                >
                                    <Power className="w-4 h-4" /> {selectedUser.isActive ? 'DEACTIVATE ENTITY' : 'ACTIVATE ENTITY'}
                                </button>
                            </div>
                            <button
                                onClick={() => setSelectedUser(null)}
                                className="px-8 py-3 bg-white/5 border border-white/10 text-white rounded-xl font-black text-[11px] uppercase tracking-widest hover:bg-white/10 hover:border-white/20 transition-all font-mono"
                            >
                                CLOSE MATRIX
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* COUPON CREATE/EDIT MODAL */}
            {isCouponModalOpen && (
                <div className="fixed inset-0 z-[60] overflow-y-auto overflow-x-hidden flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-250">
                    <div className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity" onClick={() => setIsCouponModalOpen(false)}></div>

                    <div className="relative glass-panel rounded-3xl shadow-[0_0_50px_rgba(0,0,0,0.6)] border border-white/10 w-full max-w-md overflow-hidden flex flex-col transform transition-all animate-in zoom-in-95 duration-200 bg-[#0B0F19]">
                        {/* Modal Header */}
                        <div className="bg-black/60 px-6 py-4 flex justify-between items-center text-white shrink-0 border-b border-white/10 relative overflow-hidden">
                            <div className="absolute inset-0 bg-primary/10 blur-[40px] pointer-events-none"></div>
                            <h2 className="text-lg font-black tracking-wider uppercase text-white relative z-10">
                                {couponForm.id ? 'Modify Coupon' : 'Generate Coupon'}
                            </h2>
                            <button
                                onClick={() => setIsCouponModalOpen(false)}
                                className="p-2 rounded-xl hover:bg-white/10 text-text-secondary hover:text-white transition-colors relative z-10"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleSaveCoupon} className="p-6 space-y-5 text-left">
                            <div>
                                <label className="block text-[10px] font-black text-text-secondary uppercase tracking-widest mb-1.5">Coupon Name</label>
                                <input
                                    type="text"
                                    placeholder="SAVE50"
                                    value={couponForm.couponName}
                                    onChange={(e) => setCouponForm(prev => ({ ...prev, couponName: e.target.value }))}
                                    className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-transparent text-sm font-bold text-white uppercase tracking-wider shadow-inner"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-black text-text-secondary uppercase tracking-widest mb-1.5">Discount (%)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={couponForm.discountPercent}
                                        onChange={(e) => setCouponForm(prev => ({ ...prev, discountPercent: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) }))}
                                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-transparent text-sm font-bold text-white shadow-inner"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-text-secondary uppercase tracking-widest mb-1.5">Redeem Limit</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={couponForm.redeemsCount}
                                        onChange={(e) => setCouponForm(prev => ({ ...prev, redeemsCount: Math.max(1, parseInt(e.target.value) || 1) }))}
                                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-transparent text-sm font-bold text-white shadow-inner"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black text-text-secondary uppercase tracking-widest mb-1.5">Target Plan</label>
                                <select
                                    value={couponForm.planType}
                                    onChange={(e) => setCouponForm(prev => ({ ...prev, planType: e.target.value as any }))}
                                    className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-transparent text-sm font-bold text-white shadow-inner"
                                >
                                    <option value="starter" className="bg-[#0B0F19]">Growth Plan</option>
                                    <option value="intelligence" className="bg-[#0B0F19]">Elite Plan</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <input
                                    type="checkbox"
                                    id="isActive"
                                    checked={couponForm.isActive}
                                    onChange={(e) => setCouponForm(prev => ({ ...prev, isActive: e.target.checked }))}
                                    className="w-4 h-4 rounded border-white/10 bg-black/40 text-primary focus:ring-primary/50"
                                />
                                <label htmlFor="isActive" className="text-xs font-bold text-white uppercase tracking-wider cursor-pointer select-none">
                                    Set Coupon Active
                                </label>
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/5">
                                <button
                                    type="button"
                                    onClick={() => setIsCouponModalOpen(false)}
                                    className="px-4 py-2 text-xs font-black uppercase tracking-wider text-text-secondary hover:text-white transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2.5 bg-gradient-to-r from-primary to-orange-600 rounded-xl hover:brightness-110 shadow-lg text-xs font-black uppercase tracking-wider text-white border-none"
                                >
                                    Save Coupon
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ROUTING EDIT MODAL */}
            {isRoutingModalOpen && (
                <div className="fixed inset-0 z-[60] overflow-y-auto overflow-x-hidden flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-250">
                    <div className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity" onClick={() => setIsRoutingModalOpen(false)}></div>

                    <div className="relative glass-panel rounded-3xl shadow-[0_0_50px_rgba(0,0,0,0.6)] border border-white/10 w-full max-w-md overflow-hidden flex flex-col transform transition-all animate-in zoom-in-95 duration-200 bg-[#0B0F19]">
                        <div className="px-8 py-6 bg-[#131B2C]/80 border-b border-white/10 flex justify-between items-center relative overflow-hidden backdrop-blur-xl">
                            <div className="absolute inset-0 bg-primary/10 blur-[50px] mix-blend-screen opacity-20 pointer-events-none"></div>
                            <h3 className="text-xl font-black text-white flex items-center gap-3 uppercase tracking-wide relative z-10">
                                <Activity className="w-5 h-5 text-primary" />
                                Edit Routing Model
                            </h3>
                            <button onClick={() => setIsRoutingModalOpen(false)} className="text-text-secondary hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full relative z-10">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleSaveRouting} className="p-8 space-y-6">
                            <div>
                                <label className="block text-[11px] font-black text-text-secondary uppercase tracking-widest mb-2">Task Name</label>
                                <input
                                    type="text"
                                    value={routingForm.task_name}
                                    disabled
                                    className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-sm font-bold text-text-secondary opacity-70 shadow-inner"
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] font-black text-text-secondary uppercase tracking-widest mb-2">Assigned Model ID</label>
                                <input
                                    type="text"
                                    required
                                    value={routingForm.model_id}
                                    onChange={(e) => setRoutingForm({ ...routingForm, model_id: e.target.value })}
                                    className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all text-sm font-bold text-white placeholder-text-secondary/30 shadow-inner"
                                    placeholder="e.g. gemini-1.5-flash"
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] font-black text-text-secondary uppercase tracking-widest mb-2">Fallback Models (Comma-separated)</label>
                                <input
                                    type="text"
                                    value={routingForm.fallback_models}
                                    onChange={(e) => setRoutingForm({ ...routingForm, fallback_models: e.target.value })}
                                    className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all text-sm font-medium text-white placeholder-text-secondary/30 shadow-inner"
                                    placeholder="Optional"
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] font-black text-text-secondary uppercase tracking-widest mb-2">Score (Higher Priority)</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="10"
                                    value={routingForm.score}
                                    onChange={(e) => setRoutingForm({ ...routingForm, score: Number(e.target.value) })}
                                    className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all text-sm font-bold text-white placeholder-text-secondary/30 shadow-inner"
                                    placeholder="0 to 10"
                                />
                                <p className="text-[10px] text-text-secondary mt-1 font-medium">Scores higher than others will make this provider primary for the task.</p>
                            </div>
                            <div className="pt-4 flex justify-end gap-3">
                                <button type="button" onClick={() => setIsRoutingModalOpen(false)} className="px-6 py-3 text-xs font-black uppercase tracking-widest text-text-secondary hover:text-white hover:bg-white/5 rounded-xl transition-all border border-transparent">
                                    Cancel
                                </button>
                                <button type="submit" className="px-6 py-3 bg-primary text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg hover:shadow-[0_0_20px_rgba(255,107,0,0.4)] hover:brightness-110 transition-all">
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* CONFIRMATION MODAL */}
            {confirmModal.isOpen && (
                <div className="fixed inset-0 z-[100] overflow-y-auto flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-md transition-opacity" onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}></div>
                    <div className="relative glass-panel rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/10 w-full max-w-md overflow-hidden transform transition-all animate-in zoom-in-95 duration-200">
                        <div className="p-8">
                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 border ${confirmModal.type === 'danger' ? 'bg-red-500/10 border-red-500/30 text-red-500 shadow-[0_0_15px_rgba(239,68,68,0.3)]' :
                                confirmModal.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.3)]' : 'bg-primary/10 border-primary/30 text-primary shadow-[0_0_15px_rgba(255,107,0,0.3)]'
                                }`}>
                                {confirmModal.type === 'danger' ? <ShieldAlert className="w-7 h-7" /> :
                                    confirmModal.type === 'success' ? <UserCheck className="w-7 h-7" /> :
                                        <Activity className="w-7 h-7" />}
                            </div>
                            <h3 className="text-xl font-black text-white mb-2 uppercase tracking-wide">{confirmModal.title}</h3>
                            <p className="text-text-secondary font-medium tracking-wide leading-relaxed">{confirmModal.message}</p>
                        </div>
                        <div className="bg-black/60 px-8 py-5 flex justify-end gap-4 border-t border-white/10">
                            <button
                                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                                className="px-6 py-2.5 text-[11px] font-black uppercase tracking-widest text-text-secondary hover:text-white transition-colors"
                            >
                                CANCEL
                            </button>
                            <button
                                onClick={confirmModal.onConfirm}
                                className={`px-8 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest text-white transition-all glass-panel border shadow-lg ${confirmModal.type === 'danger' ? 'bg-red-600/20 border-red-500/50 hover:bg-red-600/40 shadow-[0_0_20px_rgba(239,68,68,0.3)]' :
                                    confirmModal.type === 'success' ? 'bg-emerald-600/20 border-emerald-500/50 hover:bg-emerald-600/40 shadow-[0_0_20px_rgba(16,185,129,0.3)]' :
                                        'bg-primary/20 border-primary/50 hover:bg-primary/40 shadow-[0_0_20px_rgba(255,107,0,0.3)]'
                                    }`}
                            >
                                {confirmModal.confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
